/**
 * Mui sx styles
 */

import {
  borderRadius,
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import type { SxProps, Theme } from '@mui/material';

export const hideScrollbar: SxProps = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  // Must be the real pseudo-element `::-webkit-scrollbar`; the old
  // `::WebKitScrollbar` never matched, so WebKit kept showing the scrollbar.
  '&::-webkit-scrollbar': {
    display: 'none'
  }
};

export const truncateStyle: SxProps = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

/** A recipe-run tab whose panels scroll themselves; pair with `fillHeight`. Outermost
 * first: page box, `PageContent` (with `fullCanvas`), tab wrapper. */
export const fullHeightPageSx = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
} as const;

export const fullHeightPageContentSx = {
  flexGrow: 1,
  marginBottom: 'unset',
  height: '100%',
  overflow: 'hidden',
  minHeight: 0
} as const;

export const fullHeightTabSx = {
  px: 3,
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
} as const;

/**
 * Card surface: design-system card fill, hairline border, card radius. Shared so a card
 * and its loading skeleton can't drift apart. Spread it and add padding or
 * `overflow` for the variant you need.
 */
export const cardSurfaceSx = {
  backgroundColor: semanticColors.color.surface.raised,
  border: `1px solid ${semanticColors.color.border.primary}`,
  borderRadius: `${borderRadius.card}px`
} as const;

/**
 * A results-style pane: sunken sections (summary, options, topics) above a raised
 * list card with a header strip. Shared by the Results and Agent team tabs.
 */
export const sectionCardSx = (theme: Theme) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? semanticColors.color.surface.sunkenDeep
      : semanticColors.color.surface.sunken,
  borderRadius: `${borderRadius.s}px`,
  paddingX: `${spacing.spacing_1_1_2}px`,
  paddingY: `${spacing.spacing_1_2}px`
});

export const listCardSx = {
  position: 'relative',
  border: '1px solid',
  borderColor: semanticColors.color.border.primary,
  borderRadius: `${borderRadius.s}px`,
  overflow: 'hidden',
  bgcolor: semanticColors.color.surface.raised
} as const;

export const listCardHeaderSx = {
  alignItems: 'center',
  gap: 0.66,
  paddingX: `${spacing.spacing_1_1_2}px`,
  paddingY: `${spacing.spacing_3_4}px`,
  bgcolor: semanticColors.color.surface.raisedHeader,
  borderBottom: '1px solid',
  borderBottomColor: semanticColors.color.border.primary
} as const;

export const listCardHeaderLabelSx = {
  fontSize: typography.fontSize.xxs,
  fontWeight: typography.fontWeight.semiBold,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: semanticColors.color.text.secondary
} as const;

// Shrinks the design system's beta tag to read as a nav label.
export const navBetaTagSx = {
  fontSize: `${typography.fontSize.xxs}px`,
  fontWeight: typography.fontWeight.regular
} as const;

/**
 * Nav-bar field triggers (organization selector, search). They sit side by side
 * and must stay identical; callers layer only their own state on top.
 */
export const navFieldSx = {
  borderRadius: `${borderRadius.input}px`,
  height: `${spacing.spacing_5}px`,
  backgroundColor: semanticColors.color.surface.field,
  border: `1px solid ${semanticColors.color.border.strong}`,
  px: 2,
  py: 1.25,
  '&:hover': {
    backgroundColor: semanticColors.input.bgHover
  }
} as const;

/**
 * Top-nav icon buttons show the bare icon. `ModIconButton`'s `nav` variant
 * ships a white background and border, which the global nav doesn't use — the
 * fill appears only on hover, and persists while the popover is open so the
 * trigger still reads as active.
 *
 * Two constants rather than a factory so the sx keeps a stable identity across
 * renders. Pair with `active` on the button for its aria/selected semantics.
 */
export const topNavIconButtonSx: SxProps = {
  border: 'none',
  backgroundColor: 'transparent',
  '&:hover': {
    border: 'none',
    backgroundColor: semanticColors.color.surface.sunken
  }
};

export const topNavIconButtonActiveSx: SxProps = {
  border: 'none',
  backgroundColor: semanticColors.color.surface.sunken,
  '&:hover': {
    border: 'none',
    backgroundColor: semanticColors.color.surface.sunken
  }
};
